import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ReportsHeader from '../../../components/Reports/ReportsHeader';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import DataNotFound from '../../../components/Reports/DataNotFound';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  selectCSVWoburnReport,
  selectEmployeeData,
  selectWoburnReport,
} from '../../../store/ReportsDetailsSlice/selectors';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import { useForm } from 'react-hook-form';
import SelectRebuild from '../../../components/Inputs/SelectRebuild';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton';
import { useEffect, useMemo } from 'react';
import moment from 'moment';
import {
  getEmployeeList,
  getWoburnCSVReportData,
  getWoburnReportData,
  setWoburnCSVReportData,
} from '../../../store/ReportsDetailsSlice/actions';
import { saveCsvToFile } from '../../../utils/saveCsvToFile';
import { useWoburnColumns } from './hooks/useColumns';
import { useWoburnRows } from './hooks/useRows';
import ReusableGrid from '../../../components/Grid/index.jsx';

const defaultFormValues = {
  emp_code: { id: 'ALL', name: '' },
};

const WoburnReport = () => {
  const dispatch = useDispatch();
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);
  const { woburnReportData, isWoburnReportDataLoading } =
    useSelector(selectWoburnReport);
  const { woburnCSVData, isWoburnCSVDataLoading } = useSelector(
    selectCSVWoburnReport
  );
  const isTabletVerticalWidth = useMediaQuery('(max-width:1024px)');
  const isTabletWidth = useMediaQuery('(max-width:899px)');

  const columns = useWoburnColumns();
  const rows = useWoburnRows(woburnReportData.data);

  const { fromDate, toDate, render } = useDateRangeFilter();

  const { register, setValue, watch } = useForm({
    defaultValues: defaultFormValues,
  });

  const { emp_code: employees } = watch();

  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_emp: employees ? employees.id : 'ALL',
    };
  }, [fromDate, toDate, employees]);

  useEffect(
    () => {
      dispatch(getEmployeeList());
      dispatch(getWoburnReportData(param));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (woburnCSVData.data) {
      saveCsvToFile(woburnCSVData.data, woburnCSVData.filename);
      dispatch(setWoburnCSVReportData({ data: null, filename: '' }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woburnCSVData]);

  const handleFindClick = () => dispatch(getWoburnReportData(param));
  // CSV options
  // 0 => false
  // 1 => true
  const handleDownloadReport = () =>
    dispatch(getWoburnCSVReportData({ ...param, csv: 1 }));

  const rawFilterConfig = [
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList || []}
          name="emp_code"
          label="Employee"
          multiple={false}
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
          enableAllOption={true}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: isTabletVerticalWidth ? 'start' : 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton
            onClick={handleDownloadReport}
            disabled={isWoburnCSVDataLoading}
            text={'Export CSV'}
          />
        </Grid>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);
  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Woburn Payback Report" />

      <Box className="mt-4" sx={{ width: '100%' }}>
        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <FilterPanel
            filters={filterConfig}
            columns={isTabletVerticalWidth ? 1 : rawFilterConfig.length}
          />
        </Grid>

        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <Grid
            item
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: isTabletWidth ? 'column' : 'row',
                gap: 2,
                width: '100%',
                mt: 'auto',
              }}
            >
              {render}
            </Grid>
          </Grid>
        </Grid>

        <Box>
          {isWoburnReportDataLoading ? (
            <Box
              sx={{ minHeight: '100px' }}
              className="w-100 h-100 d-flex justify-content-center align-items-center"
            >
              <CircularProgress />
            </Box>
          ) : rows.length > 0 ? (
            <Box sx={{ display: 'flex', gap: '24px', flexDirection: 'column' }}>
              {rows.map((item) => (
                <Box
                  sx={{ display: 'flex', gap: '16px', flexDirection: 'column' }}
                >
                  <Typography variant="h5" fontWeight={600}>
                    {item.name}
                  </Typography>

                  <ReusableGrid columns={columns} rows={item.items} />
                </Box>
              ))}
            </Box>
          ) : (
            <DataNotFound />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default WoburnReport;
